import apiClient from "../common";

/** Códigos alineados a `src.simulation.models.Simulation.Status`. */
export type SimulationStatusCode =
  | "draft"
  | "running"
  | "completed"
  | "failed"
  | "archived"
  | "real";

export type SimulatedPurchaseOrderRef = {
  id: string;
  document_number: string;
};

export type SavedSimulationRow = {
  id: string;
  simulation_name: string;
  status: SimulationStatusCode;
  created_at: string;
  executed_at: string | null;
  closed_at: string | null;
  simulated_purchase_orders: SimulatedPurchaseOrderRef[];
};

function readJsonApiAttr(
  attrs: Record<string, unknown>,
  snake: string,
  kebab: string,
): unknown {
  return attrs[snake] ?? attrs[kebab];
}

function parsePurchaseOrders(raw: unknown): SimulatedPurchaseOrderRef[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.map((item) => {
    const o = item as Record<string, unknown>;
    return {
      id: String(o.id ?? ""),
      document_number: String(
        o.document_number ?? o["document-number"] ?? "",
      ),
    };
  });
}

function normalizeSimulationResource(row: Record<string, unknown>): SavedSimulationRow {
  const attrs = (row.attributes as Record<string, unknown> | undefined) ?? row;
  const id = String(row.id ?? attrs.id ?? "");
  const executed = readJsonApiAttr(attrs, "executed_at", "executed-at");
  const closed = readJsonApiAttr(attrs, "closed_at", "closed-at");
  const created = readJsonApiAttr(attrs, "created_at", "created-at");
  return {
    id,
    simulation_name: String(
      readJsonApiAttr(attrs, "simulation_name", "simulation-name") ?? "",
    ),
    status: String(attrs.status ?? "draft") as SimulationStatusCode,
    created_at: String(created ?? ""),
    executed_at:
      executed === null || executed === undefined || executed === ""
        ? null
        : String(executed),
    closed_at:
      closed === null || closed === undefined || closed === ""
        ? null
        : String(closed),
    simulated_purchase_orders: parsePurchaseOrders(
      readJsonApiAttr(
        attrs,
        "simulated_purchase_orders",
        "simulated-purchase-orders",
      ),
    ),
  };
}

export function parseSimulationsListPayload(payload: unknown): SavedSimulationRow[] {
  if (payload == null) {
    return [];
  }
  if (Array.isArray(payload)) {
    return (payload as Record<string, unknown>[]).map(normalizeSimulationResource);
  }
  const body = payload as { data?: unknown };
  if (Array.isArray(body.data)) {
    return (body.data as Record<string, unknown>[]).map(normalizeSimulationResource);
  }
  return [];
}

export async function apiListSimulations(params?: {
  status?: SimulationStatusCode;
}): Promise<SavedSimulationRow[]> {
  const res = await apiClient.get("simulations/", {
    params: params?.status !== undefined ? { status: params.status } : {},
  });
  return parseSimulationsListPayload(res.data);
}
